import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import Axios from "@/lib/axios-instance";
import useOnboardingData from "@/store/onboarding-data";
import { StorePersonality } from "./get-chatbot-prompt-settings";

export const addChatbotPromptSettings = async (payload: Omit<StorePersonality, "id">) => {
    try {
        const response = await Axios.post("/chatbot_prompt_settings/", payload);
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(`${error.response.data?.meta?.message || "An error occurred"}`);
        }
        throw new Error(error.message || "Network Error");
    }
};

export const updateChatbotPromptSettings = async (storeId: string, payload: Omit<StorePersonality, "id">) => {
    try {
        const response = await Axios.put(`/chatbot_prompt_settings/${storeId}`, payload);
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(`${error.response.data?.meta?.message || "An error occurred"}`);
        }
        throw new Error(error.message || "Network Error");
    }
};

export const useAddChatbotPromptSettings = () => {
    const queryClient = useQueryClient();
    const { storeData } = useOnboardingData();
    const storeId = storeData?.id || "";

    return useMutation({
        mutationFn: (payload: Omit<StorePersonality, "id" | "store_id">) => addChatbotPromptSettings({ ...payload, store_id: storeId }),
        onSuccess: () => {
            toast.success("Chatbot prompt settings saved successfully");
            queryClient.invalidateQueries({ queryKey: ["get_chatbot_prompt_settings", storeId] });
        },
        onError: (error) => {
            toast.error("Failed to save chatbot prompt settings", { description: error.message });
        },
    });
};

export const useUpdateChatbotPromptSettings = () => {
    const queryClient = useQueryClient();
    const { storeData } = useOnboardingData();
    const storeId = storeData?.id || "";

    return useMutation({
        mutationFn: (payload: Omit<StorePersonality, "id" | "store_id">) => updateChatbotPromptSettings(storeId, { ...payload, store_id: storeId }),
        onSuccess: () => {
            toast.success("Chatbot prompt settings updated successfully");
            queryClient.invalidateQueries({ queryKey: ["get_chatbot_prompt_settings", storeId] });
        },
        onError: (error) => {
            toast.error("Failed to update chatbot prompt settings", { description: error.message });
        },
    });
};
